import React, { createContext, useContext, useState, useCallback } from "react";
import type { ReactNode } from "react";
import { Loader, Box } from "@mantine/core";

interface TipoContextoCargando {
  cargando: boolean;
  mensaje: string;
  global: boolean;
  mostrarCargando: (mensaje?: string, global?: boolean) => void;
  ocultarCargando: () => void;
  manejarCargando: (visible: boolean, mensaje?: string, global?: boolean) => void;
}

const ContextoCargando = createContext<TipoContextoCargando | undefined>(undefined);

interface PropsProveedorCargando {
  children: ReactNode;
}

export const ProveedorCargando: React.FC<PropsProveedorCargando> = ({ children }) => {
  // Cambiamos el booleano por un contador de peticiones activas
  const [peticionesActivas, setPeticionesActivas] = useState(0);
  const [mensaje, setMensaje] = useState("");
  const [global, setGlobal] = useState(false);

  // Derivamos el estado booleano: si hay más de 0 peticiones, estamos cargando
  const cargando = peticionesActivas > 0;

  const mostrarCargando = useCallback((nuevoMensaje: string = "Cargando...", esGlobal: boolean = false) => {
    setMensaje(nuevoMensaje);
    setGlobal(esGlobal);
    setPeticionesActivas((prev) => prev + 1);
  }, []);

  const ocultarCargando = useCallback(() => {
    setPeticionesActivas((prev) => (prev > 0 ? prev - 1 : 0));
  }, []);

  const manejarCargando = useCallback((visible: boolean, nuevoMensaje: string = "Cargando...", esGlobal: boolean = false) => {
    if (visible) {
      mostrarCargando(nuevoMensaje, esGlobal);
    } else {
      ocultarCargando();
    }
  }, [mostrarCargando, ocultarCargando]);

  return (
    <ContextoCargando.Provider value={{ cargando, mensaje, global, mostrarCargando, ocultarCargando, manejarCargando }}>
      <Box style={{ position: "relative", width: "100%" }}>
        {children}
        {cargando && (
          <Box
            style={{
              position: global ? "fixed" : "absolute",
              inset: 0,
              width: global ? "100vw" : "100%",
              height: global ? "100vh" : "100%",
              backgroundColor: "rgba(255,255,255,0.7)", // Un poco más oscuro para denotar bloqueo
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 999999, // Aumentamos un nivel más por seguridad
              pointerEvents: "all", // Asegura que capture todos los clics y bloquee el fondo
            }}
            // Esto evita que se pueda hacer scroll o interactuar con lo de abajo
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center' }}>
              <Loader size="md" color="blue" />
              {mensaje && <div style={{ marginTop: '7px', color: '#646161', fontWeight: 500 }}>{mensaje}</div>}
            </div>
          </Box>
        )}
      </Box>
    </ContextoCargando.Provider>
  );
};

export const useCargando = (): TipoContextoCargando => {
  const contexto = useContext(ContextoCargando);
  if (contexto === undefined) {
    throw new Error("useCargando debe ser usado dentro de un ProveedorCargando");
  }
  return contexto;
};